import React from 'react'

import FormatCurrency from 'components/Common/FormatCurrency'
import { pluralizeToString } from 'lib/pluralize'
import Spacing from 'components/Common/Spacing'
import Pill from 'components/Luxkit/Pill'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_BED_BANK, OFFER_TYPE_LAST_MINUTE } from 'constants/offer'
import useDefaultAirport from 'hooks/useDefaultAirport'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Caption from 'components/Luxkit/Typography/Caption'
import { connect } from 'react-redux'
import { buildSuggestedDatesParamsKey } from 'lib/searchUtils'
import { getSuggestedDates } from 'selectors/offerSelectors'
import { buildSuggestedDatesString } from 'lib/searchUtils'

interface MappedStateProps {
  suggestedDates: App.OfferSuggestedDates;
}

export interface Props {
  offer: App.Offer | App.OfferSummary
  bestPricePackage?: App.Package
  filters?: App.OfferListFilters
  price: number
  duration: number
}

const hideValueOfferTypes: Array<App.OfferType> = [OFFER_TYPE_LAST_MINUTE, OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_BED_BANK]

function MapViewOfferTilePricing(props: Props & MappedStateProps) {
  const {
    offer,
    bestPricePackage,
    filters,
    price,
    duration,
    suggestedDates,
  } = props

  const hasSuggestedCheckInCheckOut = suggestedDates?.checkIn && suggestedDates?.checkOut

  const defaultAirport = useDefaultAirport()

  if (!bestPricePackage) {
    return null
  }

  const hasDates = !!filters?.checkIn
  const discountRate = Math.round(bestPricePackage.discountPercent)

  const flightPrice = offer.flightPrices[defaultAirport?.code]

  let totalPrice = suggestedDates?.price ?? price
  let totalValue = suggestedDates?.value ?? bestPricePackage.value

  if (offer.bundledWithFlightsOnly) {
    totalPrice += flightPrice
    totalValue += flightPrice
  }

  const showValue = !hideValueOfferTypes.includes(offer.type) && bestPricePackage.shouldDisplayValue && totalValue > totalPrice
  const showInclTax = !showValue

  const { tileDurationLabel, bundledWithFlightsOnly: flightsOnly } = offer

  let fromLabel = ''
  if (hasSuggestedCheckInCheckOut) {
    fromLabel = buildSuggestedDatesString(suggestedDates.checkIn, suggestedDates.checkOut)
  } else if (hasDates) {
    fromLabel = pluralizeToString('night', duration)
    fromLabel += flightsOnly ? ' + flights from ' : ' from'
  } else if (tileDurationLabel) {
    fromLabel = tileDurationLabel.toLocaleLowerCase()
    fromLabel += flightsOnly ? ' + flights from ' : ' from'
  } else {
    fromLabel = flightsOnly ? 'Hotel + flights from ' : 'From'
  }

  return (
    <>
      <BodyText variant="small" align="end">
        {fromLabel}&nbsp;
        <BodyText variant="medium" weight="bold" as="span">
          <FormatCurrency value={totalPrice} format="roundedDollar"/>
        </BodyText> /{offer.saleUnit}
      </BodyText>
      {showValue && <Caption variant="medium" weight="bold" align="end">Valued up to <FormatCurrency value={totalValue} format="roundedDollar" />
        {discountRate > 0 && <Spacing as="span" ml={4}>
          <Pill kind="primary" variant="success">{discountRate}% off</Pill>
        </Spacing>}
      </Caption>}
      {showInclTax && <Caption variant="medium" align="end" colour="neutral-three">Incl. taxes &#38; fees</Caption>}
    </>
  )
}

export default connect<MappedStateProps, undefined, Props, App.State>((appState, ownProps) => {
  const flexibleSearchFilterKey = buildSuggestedDatesParamsKey(ownProps.filters?.flexibleMonths, ownProps.filters?.flexibleNights, ownProps.filters?.rooms)
  return {
    suggestedDates: getSuggestedDates(appState, flexibleSearchFilterKey, ownProps.offer.id),
  }
})(MapViewOfferTilePricing)
